import { useEffect, useRef, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { Button, RichTextEditor } from '@/components/ui'
import { useDraftLetterBody, useStandardParagraphs } from '../api/letterQueries'
import type { PriceLetter } from '../api/letterTypes'

export interface LetterBodyEditorProps {
  letter: PriceLetter
  onSave: (html: string) => void
}

/**
 * The letter's justification text. Edits autosave after a pause. Standard
 * paragraphs append to the end; "Draft with CARLA" proposes a body that the
 * author edits (nothing is saved until they change or keep it).
 */
export function LetterBodyEditor({ letter, onSave }: LetterBodyEditorProps) {
  const locked = letter.status === 'finalized'
  const [body, setBody] = useState(letter.body_html)
  const [confirmReplace, setConfirmReplace] = useState<string | null>(null)
  const saved = useRef(letter.body_html)
  const paragraphs = useStandardParagraphs(letter.id, letter.effective_date)
  const draft = useDraftLetterBody()

  useEffect(() => {
    if (locked || body === saved.current) return
    const t = setTimeout(() => {
      saved.current = body
      onSave(body)
    }, 800)
    return () => clearTimeout(t)
  }, [body, locked, onSave])

  function applyDraft(html: string) {
    setBody(html)
    setConfirmReplace(null)
  }

  function runDraft() {
    draft.mutate(letter.id, {
      onSuccess: (res) => (body.replace(/<[^>]*>/g, '').trim() ? setConfirmReplace(res.body_html) : applyDraft(res.body_html)),
    })
  }

  return (
    <section className="flex flex-col gap-3 rounded-sm border border-border-default bg-surface p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-role-section-label m-0">2. Letter body</h2>
        {!locked ? (
          <Button variant="secondary" size="sm" onClick={runDraft} disabled={draft.isPending}>
            <Sparkles size={14} /> {draft.isPending ? 'Drafting…' : 'Draft with CARLA'}
          </Button>
        ) : null}
      </div>
      {draft.isError ? <p className="m-0 text-xs text-danger">{(draft.error as Error).message}</p> : null}
      {confirmReplace !== null ? (
        <div className="flex items-center gap-2 rounded-xs border border-warning bg-warning-bg px-3 py-2 text-sm text-warning-text">
          <span className="flex-1">CARLA wrote a draft. Replace the text you have now?</span>
          <Button size="sm" variant="primary" onClick={() => applyDraft(confirmReplace)}>
            Replace
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setConfirmReplace(null)}>
            Keep mine
          </Button>
        </div>
      ) : null}
      {!locked && paragraphs.data ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-role-metadata">Add standard paragraph:</span>
          {paragraphs.data.paragraphs.map((p) => (
            <Button key={p.key} size="sm" variant="secondary" onClick={() => setBody((b) => b + p.html)}>
              {p.label}
            </Button>
          ))}
        </div>
      ) : null}
      <RichTextEditor
        value={body}
        onChange={setBody}
        disabled={locked}
        placeholder="Why prices are changing and what the customer should expect…"
      />
      {letter.carla_draft_meta ? null : (
        <span className="text-role-metadata">
          Drafts from CARLA are a starting point. Read and edit before you finalize.
        </span>
      )}
    </section>
  )
}
